import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, X, Sparkle, CheckCircle } from "@phosphor-icons/react";

/**
 * Deliverables tab.
 *
 * Iter 85.65 · Full lifecycle control on the canonical Deliverable:
 *   • Title / Owner / Due date — click to inline-edit; Enter/blur commits,
 *     Esc cancels. Owner uses the canonical Users picker so promoted rows
 *     carry a real user_id back to the source note via the mirror sync.
 *   • Status — click the pill to cycle canonical statuses. Delivered pill
 *     lights up in Primary so completion state reads at a glance.
 *   • Mark Done — one-click shortcut on any not-yet-delivered row.
 *   • Remove — hover X to delete; parent confirms.
 * AI-generated rows (source_briefing_id present) show a Sparkle badge for
 * provenance. Every mutation flows through the parent's `save()` PUT which
 * triggers the server-side canonical → source-note mirror sync so the
 * organized-note view stays truthful.
 */
const STATUSES = ["not_started", "in_progress", "in_review", "delivered"];

export default function EngagementDeliverablesTab({
  engagement: e, dvOpen, setDvOpen, dv, setDv,
  onAddDeliverable, onCycleStatus, onOpenDeliverable,
  onEditDeliverable, onRemoveDeliverable,
  users = [],
}) {
  const [editing, setEditing] = useState(null); // {idx, field, value} | null
  const rows = e.deliverables || [];

  const userNameById = (uid) => (users.find((u) => u.id === uid) || {}).name || "";

  const startEdit = (idx, field) => {
    const cur = rows[idx];
    if (!cur) return;
    setEditing({ idx, field, value: cur[field] || "" });
  };
  const commitEdit = () => {
    if (!editing) return;
    const { idx, field, value } = editing;
    const cur = rows[idx];
    if (cur && (cur[field] || "") !== value) {
      const patch = { [field]: value };
      // When owner_id is set from the picker, also mirror the display
      // name so downstream renderers (organized note, memo) don't need
      // a Users lookup.
      if (field === "owner_id") patch.owner = userNameById(value);
      onEditDeliverable?.(idx, patch);
    }
    setEditing(null);
  };

  const markDone = (idx) => {
    onEditDeliverable?.(idx, { status: "delivered", delivered_at: new Date().toISOString() });
  };

  return (
    <div className="border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <h3 className="font-display font-bold text-sm">Deliverables</h3>
        <Dialog open={dvOpen} onOpenChange={setDvOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-none bg-primary text-primary-foreground" data-testid="add-deliverable-btn">
              <Plus size={12} className="mr-1" />Add
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle>New deliverable</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input
                placeholder="Title"
                value={dv.title}
                onChange={(v) => setDv({ ...dv, title: v.target.value })}
                className="rounded-none bg-secondary/40"
                data-testid="new-deliverable-title"
              />
              <div className="grid grid-cols-2 gap-3">
                <Select value={dv.owner_id || "none"} onValueChange={(v) => setDv({ ...dv, owner_id: v === "none" ? "" : v, owner: v === "none" ? "" : userNameById(v) })}>
                  <SelectTrigger data-testid="new-deliverable-owner" className="rounded-none bg-secondary/40"><SelectValue placeholder="Owner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  value={dv.due_date || ""}
                  onChange={(v) => setDv({ ...dv, due_date: v.target.value })}
                  data-testid="new-deliverable-due"
                  className="rounded-none bg-secondary/40"
                />
              </div>
              <Select value={dv.status || "in_progress"} onValueChange={(v) => setDv({ ...dv, status: v })}>
                <SelectTrigger className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter><Button onClick={onAddDeliverable} className="rounded-none bg-primary text-primary-foreground">Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
          <tr className="border-b border-border">
            <th className="text-left px-5 py-2 font-normal">Title</th>
            <th className="text-left px-3 py-2 font-normal">Owner</th>
            <th className="text-left px-3 py-2 font-normal">Due</th>
            <th className="text-left px-3 py-2 font-normal">Status</th>
            <th className="px-3 py-2 font-normal w-24"></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan="5" className="px-5 py-8 text-center text-xs text-muted-foreground">No deliverables yet.</td></tr>}
          {rows.map((d, i) => {
            const isAI = !!(d && (d.source_briefing_id || d.source_note_id));
            const isDone = d.status === "delivered";
            const ownerId = d.owner_id || "";
            return (
              <tr
                key={d.id || i}
                className="group border-b border-border/50 hover:bg-secondary/40 transition-colors"
                data-testid={`engagement-deliverable-row-${i}`}
              >
                <td className="px-5 py-3 font-medium">
                  {editing && editing.idx === i && editing.field === "title" ? (
                    <Input
                      autoFocus
                      value={editing.value}
                      onChange={(ev) => setEditing({ ...editing, value: ev.target.value })}
                      onBlur={commitEdit}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter") commitEdit();
                        if (ev.key === "Escape") setEditing(null);
                      }}
                      data-testid={`deliverable-title-input-${i}`}
                      className="rounded-none bg-transparent border-b border-primary/60 p-0 h-auto font-medium focus-visible:ring-0"
                    />
                  ) : (
                    <span
                      onClick={() => onEditDeliverable ? startEdit(i, "title") : onOpenDeliverable(d, i)}
                      onDoubleClick={() => onOpenDeliverable(d, i)}
                      data-testid={`deliverable-title-${i}`}
                      className={`cursor-text inline-flex items-center gap-2 ${isDone ? "line-through text-muted-foreground" : ""}`}
                    >
                      {d.title}
                      {isAI && (
                        <span
                          className="text-[0.5625rem] font-mono uppercase tracking-widest text-primary inline-flex items-center gap-0.5"
                          title="Surfaced by YadidAI"
                        >
                          <Sparkle size={9} weight="fill" /> AI
                        </span>
                      )}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-xs">
                  {editing && editing.idx === i && editing.field === "owner_id" ? (
                    <Select
                      defaultOpen
                      value={editing.value || "none"}
                      onValueChange={(v) => { setEditing({ ...editing, value: v === "none" ? "" : v }); setTimeout(commitEdit, 0); }}
                    >
                      <SelectTrigger data-testid={`deliverable-owner-select-${i}`} className="rounded-none bg-secondary/40 h-7 text-xs w-40"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span
                      onClick={() => onEditDeliverable && users.length ? startEdit(i, "owner_id") : undefined}
                      data-testid={`deliverable-owner-${i}`}
                      className="cursor-text"
                    >
                      {ownerId ? userNameById(ownerId) : (d.owner || <span className="text-muted-foreground">—</span>)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-xs">
                  {editing && editing.idx === i && editing.field === "due_date" ? (
                    <Input
                      autoFocus
                      type="date"
                      value={editing.value || ""}
                      onChange={(ev) => setEditing({ ...editing, value: ev.target.value })}
                      onBlur={commitEdit}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter") commitEdit();
                        if (ev.key === "Escape") setEditing(null);
                      }}
                      data-testid={`deliverable-due-input-${i}`}
                      className="rounded-none bg-secondary/40 h-7 text-xs w-36"
                    />
                  ) : (
                    <span
                      onClick={() => onEditDeliverable ? startEdit(i, "due_date") : undefined}
                      data-testid={`deliverable-due-${i}`}
                      className="cursor-text"
                    >
                      {d.due_date || <span className="text-muted-foreground">—</span>}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <button
                    onClick={() => onCycleStatus(i)}
                    data-testid={`deliverable-status-${i}`}
                    className={`text-[0.625rem] uppercase font-mono tracking-widest px-2 py-1 border transition ${
                      isDone
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border/60 text-muted-foreground hover:border-primary hover:text-primary"
                    }`}
                  >
                    {d.status || "in_progress"}
                  </button>
                </td>
                <td className="px-3 py-3 text-right">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!isDone && onEditDeliverable && (
                      <button
                        onClick={() => markDone(i)}
                        data-testid={`deliverable-mark-done-${i}`}
                        className="text-muted-foreground hover:text-primary p-0.5"
                        aria-label={`Mark ${d.title} delivered`}
                        title="Mark delivered"
                      >
                        <CheckCircle size={13} weight="bold" />
                      </button>
                    )}
                    {onRemoveDeliverable && (
                      <button
                        onClick={() => onRemoveDeliverable(i)}
                        data-testid={`deliverable-remove-${i}`}
                        className="text-muted-foreground hover:text-destructive p-0.5"
                        aria-label={`Remove ${d.title}`}
                        title="Remove"
                      >
                        <X size={13} weight="bold" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
